import React from 'react';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';

/**
 * Radial gauge widget — Conversion Rate, etc.
 */
// Why the backend couldn't produce a rate, in the user's terms. A conversion
// rate needs sales and footfall from the same days; when it can't be computed
// the fix depends on which half is missing, so say which.
const UNCOMPUTABLE_REASONS = {
    no_footfall_data: 'No footfall data for this period',
    no_sales_data: 'No sales data for this period',
    no_overlapping_days: 'Sales and footfall cover different dates',
    no_footfall_in_overlap: 'No footfall recorded on trading days',
};

export default function GaugeWidget({ data }) {
    const { t } = useTranslation();
    if (!data?.data) return null;
    const d = data.data;
    const raw = d.conversionRate ?? d.rate ?? d.value;

    // null means "not computable", which is not the same as zero. Rendering the
    // ?? 0 fallback would draw a confident red gauge at 0.0% over missing data.
    if (raw === null || raw === undefined) {
        const detail = UNCOMPUTABLE_REASONS[d.reason];
        return (
            <div style={{
                padding: '0 18px 14px', height: '100%', display: 'flex',
                flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
            }}>
                <div className="v3-kpi" style={{ opacity: 0.45 }}>—</div>
                <span className="v3-label" style={{ textTransform: 'none', marginTop: 4 }}>
                    {detail ? t(detail) : t('Not enough data')}
                </span>
            </div>
        );
    }

    const value = raw;
    const display = Number(value.toFixed(1));
    const color = value > 5 ? '#10B981' : value > 2 ? '#F59E0B' : '#EF4444';

    const options = {
        chart: { type: 'radialBar', fontFamily: 'Inter, system-ui' },
        plotOptions: {
            radialBar: {
                hollow: { size: '60%' },
                dataLabels: {
                    name: { show: true, fontSize: '10px', color: 'var(--v3-text-muted)', offsetY: 0 },
                    value: { show: true, fontSize: '22px', fontFamily: 'JetBrains Mono, monospace', color: 'var(--v3-text-primary)', formatter: v => `${v}%` },
                },
            },
        },
        colors: [color],
        labels: [t('Conversion')],
        stroke: { lineCap: 'round' },
    };

    return (
        <div style={{ padding: '0 8px 8px', height: '100%' }}>
            <Chart type="radialBar" options={options} series={[display]} height="100%" />
        </div>
    );
}
